import { configureStore } from '@reduxjs/toolkit';
import { combineReducers } from 'redux';
import authReducer from './slices/authSlice';
import userReducer from './slices/userSlice';
import trainingReducer from './slices/trainingSlice';
import feedReducer from './slices/feedSlice';
import publicFeedReducer from './slices/publicFeedSlice';
import beltProgressionReducer from './slices/beltProgressionSlice';
import beltConfigReducer from './slices/beltConfigSlice';
import competitionReducer from './slices/competitionSlice';
import goalsReducer from './slices/goalsSlice';
import settingsReducer from './slices/settingsSlice';
import uiReducer from './slices/uiSlice';
import injuryReducer from './slices/injurySlice';
import locationReducer from './slices/locationSlice';
import customMetricsReducer from './slices/customMetricsSlice';
import exerciseReducer from './slices/exerciseSlice';
import workoutRoutineReducer from './slices/workoutRoutineSlice';
import workoutSessionReducer from './slices/workoutSessionSlice';
import notificationReducer from './slices/notificationSlice';
import weightReducer from './slices/weightSlice';
import statusQuoReducer from './slices/statusQuoSlice';
import premiumReducer from './slices/premiumSlice';
import likesReducer from './slices/likesSlice';
import trainingWikiReducer from './slices/trainingWikiSlice';
import achievementsReducer from './slices/achievementsSlice';
import perksReducer from './slices/perksSlice';
import networkReducer from './slices/networkSlice';
import shadowbanReducer from './slices/shadowbanSlice';
import privacyReducer from './slices/privacySlice';
import feedFilterReducer from './slices/feedFilterSlice';
import errorMiddleware from './middleware/errorMiddleware';
import trainingErrorMiddleware from './middleware/trainingErrorMiddleware';
import logger from '../utils/logger';
import { setReduxStore } from '../services/firebase/feed.service';

// Create app reducer
const appReducer = combineReducers({
  auth: authReducer,
  user: userReducer,
  training: trainingReducer,
  feed: feedReducer,
  publicFeed: publicFeedReducer,
  beltProgression: beltProgressionReducer,
  beltConfig: beltConfigReducer,
  competitions: competitionReducer,
  goals: goalsReducer,
  settings: settingsReducer,
  ui: uiReducer,
  injury: injuryReducer,
  location: locationReducer,
  customMetrics: customMetricsReducer,
  exercise: exerciseReducer,
  workoutRoutine: workoutRoutineReducer,
  workoutSession: workoutSessionReducer,
  notifications: notificationReducer,
  weight: weightReducer,
  statusQuo: statusQuoReducer,
  premium: premiumReducer,
  likes: likesReducer,
  trainingWiki: trainingWikiReducer,
  achievements: achievementsReducer,
  perks: perksReducer,
  network: networkReducer,
  shadowban: shadowbanReducer,
  privacy: privacyReducer,
  feedFilter: feedFilterReducer,
  // Add more reducers as they're implemented
});

// Root reducer that can reset state on logout
const rootReducer = (state: any, action: any) => {
  // Reset all state on logout (with more explicit action type checks)
  if (
    action.type === 'auth/logout' || 
    action.type === 'auth/logoutAsync/fulfilled' ||
    action.type === 'auth/logoutAsync/rejected'
  ) {
    logger.info('Root reducer: resetting state after logout', action.type);
    
    // Keep certain non-user-specific state if needed
    const { theme } = state || {};
    state = theme ? { theme } : undefined;
  }
  
  return appReducer(state, action);
};

// No duplicate import needed

// Configure the store
export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // Add any non-serializable actions to ignore here
        ignoredActions: [
          // File uploads are not serializable
          'user/uploadUserPhoto/pending',
          'user/uploadUserPhoto/fulfilled',
          'user/uploadUserPhoto/rejected',
          'user/uploadUserCoverPhoto/pending',
          'user/uploadUserCoverPhoto/fulfilled',
          'user/uploadUserCoverPhoto/rejected',
          'training/addPhotoToTraining/pending',
          'training/addPhotoToTraining/fulfilled',
          'training/addPhotoToTraining/rejected',
          'training/addNewTraining/pending',
          'training/addNewTraining/fulfilled',
          'training/addNewTraining/rejected',
          'competitions/addCompetition/pending',
          'competitions/addCompetition/fulfilled',
          'competitions/addCompetition/rejected',
          'exercise/uploadPhoto/pending',
          'exercise/uploadPhoto/fulfilled',
          'exercise/uploadPhoto/rejected',
          'exercise/createExerciseFromForm/pending',
          'exercise/createExerciseFromForm/fulfilled',
          'exercise/createExerciseFromForm/rejected',
          'exercise/editExerciseFromForm/pending',
          'exercise/editExerciseFromForm/fulfilled',
          'exercise/editExerciseFromForm/rejected',
        ],
        // Ignore specific paths in the state that might contain non-serializable values
        ignoredPaths: ['user.isLoading', 'training.isLoading'],
      },
    }).concat(errorMiddleware, trainingErrorMiddleware),
  devTools: process.env.NODE_ENV !== 'production',
});

// Set Redux store reference for real-time Firebase updates
setReduxStore(store);

// Export types
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export type AppThunk<ReturnType = void> = import('redux-thunk').ThunkAction<
  ReturnType,
  RootState,
  unknown,
  import('redux').Action<string>
>;